/* ============================================================
   GRTRTHN® Theme — theme.js
   ============================================================ */

(function () {
  'use strict';

  /* ── Helpers ───────────────────────────────────────────────── */
  function $(selector, context) {
    return (context || document).querySelector(selector);
  }

  function $$(selector, context) {
    return Array.from((context || document).querySelectorAll(selector));
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Cart state ─────────────────────────────────────────────── */
  const CartAPI = {
    async getCart() {
      const res = await fetch('/cart.js');
      return res.json();
    },

    async addItem(variantId, quantity = 1) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      return res.json();
    },

    async updateItem(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      return res.json();
    },

    async removeItem(key) {
      return CartAPI.updateItem(key, 0);
    }
  };

  /* ── Toast notifications ────────────────────────────────────── */
  const Toast = {
    el: null,
    timeout: null,

    init() {
      this.el = document.createElement('div');
      this.el.className = 'toast';
      this.el.setAttribute('role', 'status');
      this.el.setAttribute('aria-live', 'polite');
      document.body.appendChild(this.el);
    },

    show(message, duration = 2500) {
      if (!this.el) this.init();
      clearTimeout(this.timeout);
      this.el.textContent = message;
      this.el.classList.add('is-visible');
      this.timeout = setTimeout(() => {
        this.el.classList.remove('is-visible');
      }, duration);
    }
  };

  /* ── Cart Drawer ────────────────────────────────────────────── */
  const CartDrawer = {
    drawer: null,
    body: null,
    footer: null,
    countEl: null,

    init() {
      this.drawer = $('#cart-drawer');
      this.body = $('#cart-drawer-body');
      this.footer = $('#cart-drawer-footer');
      this.countEl = $('#cart-count');

      if (!this.drawer) return;

      // Open triggers
      $$('#cart-toggle, [data-open-cart]').forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      // Close triggers
      const closeBtn = $('#cart-drawer-close');
      const overlay = $('#cart-drawer-overlay');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      if (overlay) overlay.addEventListener('click', () => this.close());

      // ESC key
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    },

    async open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      await this.render();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    async render() {
      if (!this.body) return;
      try {
        const cart = await CartAPI.getCart();
        this.updateCount(cart.item_count);

        if (cart.item_count === 0) {
          this.body.innerHTML = '<p class="drawer-empty">Your cart is empty.</p>';
          this.footer.innerHTML = '';
          return;
        }

        // Items
        this.body.innerHTML = cart.items.map(item => `
          <div class="drawer-item" data-item-key="${item.key}">
            ${item.image
              ? `<img src="${item.image.replace(/\.(jpg|png|gif|webp)/, '_120x120.$1')}" alt="${item.title}" class="drawer-item__image" loading="lazy" width="72" height="72">`
              : '<div class="drawer-item__image"></div>'
            }
            <div class="drawer-item__info">
              <p class="drawer-item__title">${item.product_title}</p>
              ${item.variant_title && item.variant_title !== 'Default Title'
                ? `<p class="drawer-item__variant">${item.variant_title}</p>`
                : ''
              }
              <div class="drawer-item__bottom">
                <span class="drawer-item__price">${formatMoney(item.line_price)}</span>
                <button class="drawer-item__remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">Remove</button>
              </div>
            </div>
          </div>
        `).join('');

        // Footer
        this.footer.innerHTML = `
          <div class="drawer-subtotal">
            <span>Subtotal</span>
            <span class="drawer-subtotal__price">${formatMoney(cart.total_price)}</span>
          </div>
          <a href="/checkout" class="btn-checkout">Checkout</a>
          <a href="/cart" style="display:block;text-align:center;margin-top:10px;font-size:0.75rem;letter-spacing:0.15em;text-transform:uppercase;color:var(--color-text-muted);text-decoration:underline;text-underline-offset:3px;">View Cart</a>
        `;

        // Remove listeners
        $$('.drawer-item__remove', this.body).forEach(btn => {
          btn.addEventListener('click', async () => {
            const key = btn.dataset.key;
            await CartAPI.removeItem(key);
            await this.render();
          });
        });

      } catch (err) {
        console.error('Cart drawer error:', err);
      }
    },

    updateCount(count) {
      if (this.countEl) {
        this.countEl.textContent = count > 0 ? count : '';
      }
    }
  };

  /* ── Quick Add ──────────────────────────────────────────────── */
  function initQuickAdd() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.product-card__quick-add');
      if (!btn) return;

      const variantId = btn.dataset.productId;
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('loading');

      try {
        await CartAPI.addItem(variantId, 1);
        const cart = await CartAPI.getCart();
        CartDrawer.updateCount(cart.item_count);
        Toast.show('Added to cart');
        CartDrawer.open();
      } catch (err) {
        Toast.show('Could not add to cart');
        console.error(err);
      } finally {
        btn.disabled = false;
        btn.classList.remove('loading');
      }
    });
  }

  /* ── Product Page ───────────────────────────────────────────── */
  function initProductPage() {
    const form = $('#product-form');
    if (!form) return;

    // Variant selector
    const optionBtns = $$('.option-btn');
    if (optionBtns.length) {
      optionBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const index = parseInt(btn.dataset.optionIndex);
          const value = btn.dataset.optionValue;

          // Update active state within option group
          $$(`[data-option-index="${index}"]`).forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');

          // Find matching variant
          updateVariant();
        });
      });
    }

    function updateVariant() {
      const selectedOptions = [];
      $$('.product-page__option').forEach((optGroup, i) => {
        const selected = $('[data-option-index].is-selected', optGroup);
        if (selected) selectedOptions.push(selected.dataset.optionValue);
      });

      // Try to find variant from Shopify's product JSON (if available)
      const productData = window.__product_data__;
      if (!productData) return;

      const variant = productData.variants.find(v => {
        return selectedOptions.every((opt, i) => v.options[i] === opt);
      });

      if (variant) {
        const variantInput = $('#product-variant-id');
        const priceEl = $('#product-price');
        const addBtn = $('#add-to-cart-btn');

        if (variantInput) variantInput.value = variant.id;

        if (priceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceEl.innerHTML = `
              <s class="product-page__price--compare">${formatMoney(variant.compare_at_price)}</s>
              <span class="product-page__price--sale">${formatMoney(variant.price)}</span>
            `;
          } else {
            priceEl.innerHTML = `<span>${formatMoney(variant.price)}</span>`;
          }
        }

        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
        }

        // Update main image if variant has featured image
        if (variant.featured_image) {
          const mainImg = $('#product-main-image');
          if (mainImg) {
            mainImg.src = variant.featured_image.src.replace(/\.(jpg|png|gif|webp)/, '_900x.$1');
            mainImg.alt = variant.featured_image.alt || '';
          }
        }
      }
    }

    // Gallery thumbnails
    $$('.product-gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const mainImg = $('#product-main-image');
        if (mainImg) {
          mainImg.src = thumb.dataset.imageUrl;
          mainImg.alt = thumb.dataset.imageAlt;
        }
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    // Qty buttons (product page)
    const qtyInput = $('#product-qty');
    if (qtyInput) {
      document.querySelector('.qty-btn--minus')?.addEventListener('click', () => {
        const val = parseInt(qtyInput.value) || 1;
        if (val > 1) qtyInput.value = val - 1;
      });
      document.querySelector('.qty-btn--plus')?.addEventListener('click', () => {
        const val = parseInt(qtyInput.value) || 1;
        qtyInput.value = val + 1;
      });
    }

    // Add to cart intercept (AJAX)
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const addBtn = $('#add-to-cart-btn');
      const variantId = $('#product-variant-id')?.value;
      const quantity = parseInt($('#product-qty')?.value) || 1;

      if (!variantId) return;

      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';

      try {
        await CartAPI.addItem(variantId, quantity);
        const cart = await CartAPI.getCart();
        CartDrawer.updateCount(cart.item_count);
        addBtn.textContent = 'Added!';
        setTimeout(() => {
          addBtn.textContent = 'Add to cart';
          addBtn.disabled = false;
        }, 1500);
        CartDrawer.open();
      } catch (err) {
        addBtn.textContent = 'Error — try again';
        addBtn.disabled = false;
        console.error(err);
      }
    });
  }

  /* ── Cart Page ──────────────────────────────────────────────── */
  function initCartPage() {
    if (!$('.cart-page')) return;

    // Qty buttons
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.cart-item .qty-btn');
      if (!btn) return;

      const item = btn.closest('.cart-item');
      const key = item?.dataset.itemKey;
      const input = item?.querySelector('.qty-input');
      if (!key || !input) return;

      let qty = parseInt(input.value) || 1;
      if (btn.classList.contains('qty-btn--minus')) qty = Math.max(0, qty - 1);
      if (btn.classList.contains('qty-btn--plus')) qty += 1;

      input.value = qty;
      if (qty === 0) item.style.opacity = '0.4';

      await CartAPI.updateItem(key, qty);
      if (qty === 0) {
        item.remove();
      }
      const cart = await CartAPI.getCart();
      CartDrawer.updateCount(cart.item_count);
      const subtotalEl = $('#cart-subtotal');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    });

    // Remove buttons
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.cart-item__remove');
      if (!btn) return;
      const key = btn.dataset.key;
      const item = btn.closest('.cart-item');
      if (item) item.style.opacity = '0.4';
      await CartAPI.removeItem(key);
      if (item) item.remove();
      const cart = await CartAPI.getCart();
      CartDrawer.updateCount(cart.item_count);
      const subtotalEl = $('#cart-subtotal');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    });
  }

  /* ── Mobile menu ────────────────────────────────────────────── */
  function initMobileMenu() {
    const toggle = $('#menu-toggle');
    const nav = $('#mobile-nav');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', () => {
      const isOpen = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen);
      nav.setAttribute('aria-hidden', !isOpen);
    });
  }

  /* ── Init ───────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
    Toast.init();
    initQuickAdd();
    initProductPage();
    initCartPage();
    initMobileMenu();
  });

})();
